// src/security/permissions.router.ts
import type { MemberContext, NavItem, ActionId, FixtureContext, FineEventContext } from "./permissions";
import { hasRole, hasAnyRole, canSeeFinance, canSeeFines, isCaptainForTeam } from "./permissions";

export const Routes = {
  home: "/",
  login: "/login",
  signUp: "/sign-up",
  join: "/join",
  settings: "/settings",
  availability: "/availability",
  fixtures: "/fixtures",
  results: "/results",
  stats: "/stats",
  leaderboard: "/leaderboard",
  badges: "/badges",
  badgeDetail: "/badge-detail",
  myTeam: "/my-team",
  teamDetail: (teamId: string) => `/team-detail/${teamId}`,
  league: "/league",
  profile: "/profile",
  playerProfile: "/player-profile",
  selectFixturePage: "/select-fixture-page",
  selectedTeamPage: "/selected-team-page",
  selectedOppoTeam: "/selected-oppo-team",
  selection: "/selection",
  enterFixture: "/enter-fixture",
  statsEntry: "/stats-entry",
  scoreEntry: "/score-entry",
  matchEntry: "/match-entry",
  finance: "/finance",
  userFinance: "/user-finance",
  financePlayer: "/finance-player",
  financeAdmin: "/finance-admin",
  fineEntry: "/fine-entry",
  fines: "/fines",
  newsEntry: "/news-entry",
  newsDetails: (newsId: string) => `/news/${newsId}`,
  clubStore: "/club-store",
  laws: "/laws",
  awards: "/awards",
  events: "/events",
  enterEvent: "/enter-event",
  supportTicket: "/support-ticket",
  supportAdmin: "/support-admin",
  admin: "/admin",
  adminUpdateStrikeRate: "/admin-update-strike-rate",
  mergePlayerData: "/merge-player-data",
  statsUpload: "/stats-upload",
  paymentsTest: "/payments",
  terms: "/terms",
  fantasyRules: "/fantasy-rules",
  fantasyEntry: "/fantasy-entry",
  socialScoreboard: "/social-scoreboard",
} as const;

export type AppPath = (typeof Routes)[keyof typeof Routes] | string;

export type GuardDecision =
  | { allow: true }
  | { allow: false; redirectTo: string };

export function guardRoute(member: MemberContext | null, path: string): GuardDecision {
  const publicPaths = new Set<string>(["/login", "/terms", "/join"]);
  if (publicPaths.has(path)) return { allow: true };
  if (!member) return { allow: false, redirectTo: "/login" };

  const adminOnlyPaths = new Set<string>([
    "/admin",
    "/merge-player-data",
    "/admin-invites",
    "/admin-players",
    "/admin-members",
    "/admin-teams",
    "/admin-club-settings",
    "/admin-bulk-invites",
    "/admin-seasons",
    "/admin-update-strike-rate",
  ]);

  if (adminOnlyPaths.has(path) && !hasRole(member, "Admin")) {
    return { allow: false, redirectTo: "/" };
  }

  return { allow: true };
}

export function buildNavFromExistingRoutes(
  m: MemberContext,
  opts?: { paymentsBadge?: number | string }
): NavItem[] {
  const items: NavItem[] = [
    { id: "home", label: "Home", section: "main", icon: "home" },
    { id: "fixtures", label: "Fixtures", section: "main", icon: "calendar" },
    { id: "stats", label: "Stats", section: "main", icon: "chart" },
    { id: "squad", label: "Squad", section: "main", icon: "users" },
    { id: "announcements", label: "Announcements", section: "more", icon: "megaphone" },
  ];

  if (m.modules.financeEnabled) {
    items.push({
      id: "myPayments",
      label: "My Payments",
      section: "more",
      icon: "wallet",
      badge: opts?.paymentsBadge,
    });
  }

  if (m.modules.finesEnabled) {
    items.push({ id: "finesFeed", label: "Fines", section: "more", icon: "receipt" });
  }

  items.push({ id: "settings", label: "Settings", section: "more", icon: "cog" });

  if (hasAnyRole(m, ["Captain", "Admin"])) {
    items.push({ id: "captainSelection", label: "Selection", section: "captain", icon: "checklist" });
    items.push({ id: "captainMatchEntry", label: "Match Entry", section: "captain", icon: "edit" });
  }

  if (canSeeFinance(m)) {
    items.push({ id: "financeOverview", label: "Finance", section: "finance", icon: "bank" });
    items.push({ id: "financePlayerAccounts", label: "Player Accounts", section: "finance", icon: "id" });
    items.push({ id: "financeTransactions", label: "Transactions", section: "finance", icon: "swap" });
  }

  if (hasRole(m, "Admin")) {
    items.push({ id: "adminPlayerManagement", label: "Admin Tools", section: "admin", icon: "shield" });
  }

  return items;
}

export function navItemToPath(navId: NavItem["id"]): string {
  switch (navId) {
    case "home":
      return Routes.home;
    case "fixtures":
      return Routes.fixtures;
    case "stats":
      return Routes.stats;
    case "squad":
      return Routes.myTeam;
    case "announcements":
      return Routes.home;
    case "settings":
      return Routes.settings;
    case "myPayments":
      return Routes.userFinance;
    case "finesFeed":
      return Routes.fines;
    case "captainSelection":
      return Routes.selection;
    case "captainMatchEntry":
      return Routes.matchEntry;
    case "financeOverview":
      return Routes.finance;
    case "financePlayerAccounts":
      return Routes.financePlayer;
    case "financeTransactions":
      return Routes.financeAdmin;
    case "adminPlayerManagement":
      return Routes.admin;
    default:
      return Routes.home;
  }
}

export function canPerformExistingAction(
  m: MemberContext,
  action: ActionId,
  ctx?: { fixture?: FixtureContext; fine?: FineEventContext }
): boolean {
  switch (action) {
    case "selection:write":
    case "matchEntry:write": {
      const teamId = ctx?.fixture?.teamId;
      if (!teamId) return hasRole(m, "Admin");
      return isCaptainForTeam(m, teamId);
    }
    case "fineEvent:create": {
      if (!canSeeFines(m)) return false;
      const teamId = ctx?.fine?.teamId ?? ctx?.fixture?.teamId;
      if (!teamId) return hasRole(m, "Admin");
      return isCaptainForTeam(m, teamId);
    }
    default:
      return true;
  }
}
